import { z } from 'zod';

// Appointment outcome reported by an authorized human or synced from the CRM (setter spec §05.2, §10.5, §15.7).
// Kept in its own module so api.ts (AppointmentSchema) and api-appointment-loop.ts can both import it without a cycle.

export const APPOINTMENT_OUTCOMES = ['completed', 'no_show', 'cancelled', 'rescheduled'] as const;
export const AppointmentOutcomeSchema = z.enum(APPOINTMENT_OUTCOMES);
export type AppointmentOutcome = z.infer<typeof AppointmentOutcomeSchema>;

export const APPOINTMENT_OUTCOME_LABELS: Record<AppointmentOutcome, string> = {
  completed: 'Realizada',
  no_show: 'No show',
  cancelled: 'Cancelada',
  rescheduled: 'Reprogramada',
};

/** Fixed funnel stage each outcome moves the opportunity to (funnel/machine.ts applies it). */
export const APPOINTMENT_OUTCOME_STAGE = {
  completed: 'appointment_completed',
  no_show: 'no_show',
  cancelled: 'appointment_cancelled',
  rescheduled: 'appointment_rescheduled',
} as const satisfies Record<AppointmentOutcome, string>;

export const APPOINTMENT_OUTCOME_SOURCES = ['human', 'crm'] as const;
export const AppointmentOutcomeSourceSchema = z.enum(APPOINTMENT_OUTCOME_SOURCES);
export type AppointmentOutcomeSource = z.infer<typeof AppointmentOutcomeSourceSchema>;

/** Reminder kinds of the single built-in reminder mechanism (C8). Legacy rows keep a `legacy_<id>` kind. */
export const REMINDER_KINDS = ['confirmation', 'day_before', 'hours_before'] as const;
export type ReminderKind = (typeof REMINDER_KINDS)[number];
export const REMINDER_KIND_LABELS: Record<ReminderKind, string> = {
  confirmation: 'Confirmación al agendar',
  day_before: 'Un día antes',
  hours_before: 'Horas antes',
};

/** WhatsApp number of a team member for booking notices (normalized to E.164 digits by the backend). */
export const AdvisorPhoneSchema = z
  .string()
  .trim()
  .regex(/^\+?[0-9 ()-]{8,20}$/, 'Usa un número con lada internacional, p. ej. +52 81 1234 5678');

/**
 * `appointment.outcome_recorded` (setter §10.5) as consumed by recovery cadences (Wave 2B): no-show / cancellation
 * cadences start from it. Outcome vocabulary of the event: `held` = Realizada (internal `completed`).
 * Carried in the outgoing webhook payload as `data.event`, next to the snake_case appointment fields.
 */
export const APPOINTMENT_EVENT_OUTCOMES = ['held', 'no_show', 'cancelled', 'rescheduled'] as const;
export type AppointmentEventOutcome = (typeof APPOINTMENT_EVENT_OUTCOMES)[number];
export const APPOINTMENT_EVENT_OUTCOME: Record<AppointmentOutcome, AppointmentEventOutcome> = {
  completed: 'held',
  no_show: 'no_show',
  cancelled: 'cancelled',
  rescheduled: 'rescheduled',
};
export const AppointmentOutcomeRecordedEventSchema = z.object({
  workspaceId: z.string(),
  leadInterestId: z.string().nullable(),
  contactId: z.string(),
  projectId: z.string(),
  appointmentId: z.string(),
  outcome: z.enum(APPOINTMENT_EVENT_OUTCOMES),
});
export type AppointmentOutcomeRecordedEvent = z.infer<typeof AppointmentOutcomeRecordedEventSchema>;
